/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useState } from 'react'
import Highlighter from 'react-highlight-words'
import { Link } from 'react-router-dom'

import {
  KnowledgeBase,
  LIST_NAME as KnowledgeBaselist,
} from '../../../assets/ts/data/knowledge-base'
import { formatUTCDateToLocal } from '../../../assets/ts/date-and-time/date-and-time'
import { openFile } from '../../../assets/ts/files/files'
import { DocumentIconNames, removeTags } from '../../../assets/ts/general/general'
import NO_RESULTS from '../../../utils/constants/no-results'
import { NORESULT } from '../../../utils/constants/rhybus-config'
import InputSearch from '../../../utils/controls/input-search'

interface IRelatedKnowledgeBaseProps {
  filteredKnowledgeBase: any
  setKnowledgeBaseCount: React.Dispatch<React.SetStateAction<number>>
  userInputs: any // TODO -- change type to userInput interface once functionality is implemented
}
interface IKnowledgeBase extends KnowledgeBase {
  eKey: string
  extra: string
  id: number
  link: string
  postedDate: string
  showMore: boolean
  subcontent: string
}
const RelatedKnowledgeBase = ({
  filteredKnowledgeBase,
  setKnowledgeBaseCount,
}: IRelatedKnowledgeBaseProps) => {
  const [toggleShowMore, setToggleShowMore] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState<string>('')
  // Filter knowledge base data by the search value
  const searchedKnowledgeBase: IKnowledgeBase[] = filteredKnowledgeBase.filter(
    (knowledgeBaseEntry: IKnowledgeBase) => {
      const createdBy = knowledgeBaseEntry?.ItemCreatedBy
      const attachments: string[] =
        knowledgeBaseEntry?.Attachments[0]?.blobs?.flatMap((blob: any) => {
          return blob?.fileName
        }) || []
      return (
        knowledgeBaseEntry.Title.toLowerCase().includes(searchValue) ||
        removeTags(knowledgeBaseEntry.Description).toLowerCase().includes(searchValue) ||
        knowledgeBaseEntry.Category.some((cat) => cat.toLowerCase().includes(searchValue)) ||
        knowledgeBaseEntry.Section.some((sec) => sec.toLowerCase().includes(searchValue)) ||
        formatUTCDateToLocal(knowledgeBaseEntry.ItemCreated).toLowerCase().includes(searchValue) ||
        createdBy?.properties?.defaultDisplay.toLowerCase().includes(searchValue) ||
        attachments?.some((filename: string) => filename.toLowerCase().includes(searchValue))
      )
    }
  )
  setKnowledgeBaseCount(searchedKnowledgeBase.length)

  /**
   * Updates the searchValue state when the search input is changed.
   * @param {React.ChangeEvent<HTMLInputElement>} e Input obj returned from search input
   */
  const handleSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value.toLowerCase())
  }

  return (
    <>
      <div aria-label={NORESULT} className='subtitle-color2 montserratregular d-none' tabIndex={0}>
        {NORESULT}
      </div>
      <div className='py-3 border-none'>
        <div className='mx-2 mb-1'>
          <InputSearch
            aria-label='Search'
            className='border w-100 form-lg border-radius03 font-13 sourcesanspro'
            icon='icon-search pe-2 title-color'
            inputProps={{
              placeholder: 'Search Knowledge Base',
            }}
            onChange={handleSearchValue}
            searchgroup=' w-100 d-flex align-items-center inputswithicon'
          />
        </div>
        {searchedKnowledgeBase.length === 0 ? (
          <div className='py-3 border-none'>
            <div>
              <NO_RESULTS />
            </div>
          </div>
        ) : (
          searchedKnowledgeBase.map((knowledgeBaseEntry) => (
            <div className='mb-2 pb-1 border-bottom1 kb' key={knowledgeBaseEntry.Title}>
              <p className='m-0'>
                <Link
                  className='title-color1 font-13 poppins-semibold  lineClamp1 maxfitcontent px-2'
                  style={{ cursor: 'text' }}
                  title={knowledgeBaseEntry.Title}
                  to='#'
                >
                  <Highlighter
                    highlightClassName='YourHighlightClass'
                    searchWords={[searchValue]}
                    textToHighlight={knowledgeBaseEntry.Title}
                  />
                </Link>
              </p>

              <p
                aria-label={knowledgeBaseEntry.Description}
                className='m-0 py-1 lh-2 sourcesanspro font-13 title-color19 maxfitcontent px-2'
                tabIndex={0}
              >
                {knowledgeBaseEntry.showMore ? (
                  <Highlighter
                    highlightClassName='YourHighlightClass'
                    searchWords={[searchValue]}
                    textToHighlight={removeTags(knowledgeBaseEntry.Description)}
                  />
                ) : (
                  <Highlighter
                    highlightClassName='YourHighlightClass'
                    searchWords={[searchValue]}
                    textToHighlight={removeTags(
                      `${knowledgeBaseEntry.Description.substring(0, 220)}`
                    )}
                  />
                )}
              </p>

              {knowledgeBaseEntry?.Attachments &&
                knowledgeBaseEntry.showMore &&
                knowledgeBaseEntry?.Attachments[0]?.blobs.length > 0 &&
                knowledgeBaseEntry?.Attachments[0].blobs.map((blob: any) => (
                  <div className='attachmentsfiles' key={blob.fileName}>
                    <a
                      className='py-1 d-inline-block max-fitcontent title-color12 sourcesanspro font-13 me-2 links'
                      href='#'
                      key={blob.fileName}
                      onClick={() => openFile(blob, knowledgeBaseEntry.eKey, KnowledgeBaselist)}
                      tabIndex={0}
                      title={blob.fileName}
                    >
                      <span className='color-primary'>
                        <span className={`${DocumentIconNames(blob.fileName)} pe-2`}></span>
                        <Highlighter
                          highlightClassName='YourHighlightClass'
                          searchWords={[searchValue]}
                          textToHighlight={blob.fileName}
                        />
                      </span>
                    </a>
                  </div>
                ))}
              <div>
                {(knowledgeBaseEntry.Description.length > 220 ||
                  knowledgeBaseEntry.Attachments.length > 0) && (
                  <Link
                    className='py-1 d-inline-block max-fitcontent title-color12 sourcesanspro font-13 links px-2 border-0'
                    onClick={() => {
                      setToggleShowMore(!toggleShowMore)
                      knowledgeBaseEntry.showMore = !knowledgeBaseEntry.showMore
                    }}
                    title={knowledgeBaseEntry.showMore ? 'View Less' : 'View More'}
                    to='#'
                  >
                    {knowledgeBaseEntry.showMore ? 'View Less' : 'View More'}
                  </Link>
                )}
              </div>

              <p
                aria-label={knowledgeBaseEntry.Title}
                className='m-0 sourcesanspro subtitle-color font-13 maxfitcontent px-2'
                tabIndex={0}
              >
                <Highlighter
                  highlightClassName='YourHighlightClass'
                  searchWords={[searchValue]}
                  textToHighlight={knowledgeBaseEntry.subcontent}
                />
              </p>
            </div>
          ))
        )}
      </div>
    </>
  )
}
export default RelatedKnowledgeBase
